import { Document, Page, pdfjs } from 'react-pdf';

import { useLocation } from 'react-router-dom';

import './styles.scss';

import { useReport } from './use-report';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',

  import.meta.url
).toString();

type ReportPageProps = {
  requireEmailGate?: boolean;
};

export const ReportPage = ({ requireEmailGate = true }: ReportPageProps) => {
  const location = useLocation();

  const {
    pdfUrl,
    numPages,
    setNumPages,
    pageNumber,
    canGoPrev,
    canGoNext,
    requestPrevPage,
    requestNextPage,
    gateOpen,
    setGateOpen,
    email,
    emailError,
    isEmailPending,
    handleChangeEmail,
    handleSubmitEmail,
  } = useReport({ requireEmailGate });

  return (
    <main className='report-page'>
      <div className='report-viewer'>
        <div className='report-stage'>
          <div className='report-controls' aria-label='PDF controls'>
            <button
              className='report-button'
              onClick={requestPrevPage}
              disabled={!canGoPrev}
            >
              Prev
            </button>

            <div className='report-page-indicator'>
              Page <strong>{pageNumber}</strong> / {numPages ?? '…'}
            </div>

            <button
              className='report-button'
              onClick={requestNextPage}
              disabled={!canGoNext}
            >
              Next
            </button>
          </div>

          <div className='report-pdf-container'>
            <Document
              key={`${location.pathname}-${pdfUrl}`}
              className='report-pdf-document'
              file={pdfUrl}
              onLoadSuccess={({ numPages: nextNumPages }) =>
                setNumPages(nextNumPages)
              }
              loading={<div className='report-loading'>Loading PDF…</div>}
              error={<div className='report-error'>Couldn’t load PDF. </div>}
              noData={
                <div className='report-error'>No PDF URL configured.</div>
              }
            >
              <Page
                pageNumber={pageNumber}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                className='report-pdf-page'
              />
            </Document>
          </div>
        </div>

        {requireEmailGate && gateOpen && (
          <div className='report-gate-overlay' role='dialog' aria-modal='true'>
            <div className='report-gate-modal'>
              <h2 className='report-gate-title'>
                Enter your email to continue
              </h2>

              <p className='report-gate-subtitle'>
                You can preview up to 3 pages. To view the rest, please provide
                your email.
              </p>

              <form onSubmit={handleSubmitEmail} className='report-gate-form'>
                <label className='report-gate-label'>
                  <span className='report-gate-label-text'>Email</span>

                  <input
                    className='report-gate-input'
                    value={email}
                    disabled={isEmailPending}
                    onChange={e => {
                      handleChangeEmail(e.target.value);
                    }}
                    placeholder='you@company.com'
                    inputMode='email'
                    autoComplete='email'
                  />
                </label>

                {emailError ? (
                  <div className='report-gate-error'>{emailError}</div>
                ) : null}

                <div className='report-gate-actions'>
                  <button
                    className='report-gate-submit'
                    type='submit'
                    disabled={isEmailPending}
                  >
                    {isEmailPending ? 'Submitting…' : 'Continue'}
                  </button>

                  <button
                    className='report-gate-cancel'
                    type='button'
                    disabled={isEmailPending}
                    onClick={() => setGateOpen(false)}
                  >
                    Not now
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};
